import {
  contextTokensFromUsage,
  deriveCacheUsage,
  deriveContextUsage,
  isSubagentFrame,
} from '@reflex/shared';
import type {
  AxonEvent,
  ContextCacheUsage,
  ContextUsage,
  ContextUsageTokenInput,
} from '@reflex/shared';
import { CODEX_TOKEN_USAGE_EVENT_TYPE, classifyEvent } from './timeline-helpers.js';

/**
 * Read the usage an `assistant` message payload carries, nested under
 * `message.usage`. The shape is the shared `ContextUsageTokenInput` (the
 * protocol's `AssistantUsage` token fields). Returns `undefined` when absent.
 */
function usageFromClaudePayload(data: Record<string, unknown>): ContextUsageTokenInput | undefined {
  const message = data.message as { usage?: ContextUsageTokenInput } | undefined;
  return message?.usage;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function positiveNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Derive a context-usage snapshot from a native Codex
 * `thread/tokenUsage/updated` frame. `last` is the most recent model
 * request's breakdown; its `inputTokens` (which already includes the cached
 * prefix) is the input-side size of the current context, mirroring the
 * Claude derivation. The frame carries the model's own `modelContextWindow`,
 * which wins over the registry-supplied `maxTokens` fallback.
 *
 * Codex reports only the cached subset of the input (`cachedInputTokens`), not
 * cache writes or their TTL, so the cache breakdown it produces leaves those
 * at zero and omits `ttlTokens` rather than inventing figures.
 */
function usageFromCodexPayload(
  data: Record<string, unknown>,
  maxTokens: number | null | undefined,
): ContextUsage | null {
  const tokenUsage = asRecord(asRecord(data.params)?.tokenUsage);
  if (!tokenUsage) return null;
  const last = asRecord(tokenUsage.last);
  const used = positiveNumber(last?.inputTokens);
  if (used === null) return null;
  return deriveContextUsage(
    used,
    positiveNumber(tokenUsage.modelContextWindow) ?? maxTokens,
    codexCacheUsage(used, positiveNumber(last?.cachedInputTokens)),
  );
}

/**
 * Build the cache breakdown for a Codex request. `cachedInputTokens` is a
 * subset of `inputTokens` (unlike Claude, where the cache fields sit alongside
 * the fresh input), so the uncached remainder is the difference. Returns
 * `null` when nothing was served from cache, matching
 * {@link deriveCacheUsage}'s "no cache activity, no stats" rule.
 */
function codexCacheUsage(
  inputTokens: number,
  cachedTokens: number | null,
): ContextCacheUsage | null {
  if (cachedTokens === null) return null;
  const readTokens = Math.min(cachedTokens, inputTokens);
  return {
    readTokens,
    writeTokens: 0,
    freshTokens: inputTokens - readTokens,
    fraction: readTokens / inputTokens,
  };
}

/**
 * Find the most recent token usage in an agent's event stream and turn it
 * into a context-usage snapshot against `maxTokens`. Scans newest-first for a
 * Claude `assistant` message's usage (whose input + cache tokens size the
 * context for that single request) or a native Codex
 * `thread/tokenUsage/updated` frame. Classification reuses
 * {@link classifyEvent} so the `turn.claude.*` prefix handling stays in one
 * place. Returns `null` when no usage has been reported yet or no usable
 * context window is known, so the caller can simply omit the indicator.
 *
 * A turn `result`'s `usage` is deliberately ignored: the SDK reports it as the
 * session-cumulative total (summed across every turn, alongside
 * `total_cost_usd`/`num_turns`), not a point-in-time context size. Reading it
 * would sum many turns of input + cache tokens and, once a run ends with a
 * trailing `result`, push `usedTokens` past the model's window — showing the
 * meter as "over the max," which a single context can never be.
 *
 * Subagent (Task) `assistant` messages are also skipped: they carry the
 * *subagent's* per-request usage, and only main-thread requests size the
 * composer's meter. See {@link isSubagentFrame}.
 */
export function latestContextUsage(
  events: readonly AxonEvent[] | undefined,
  maxTokens: number | null | undefined,
): ContextUsage | null {
  if (!events?.length) return null;
  for (const event of [...events].reverse()) {
    const { kind, data } = classifyEvent(event);
    if (kind === 'codex_protocol') {
      if (data.type !== CODEX_TOKEN_USAGE_EVENT_TYPE) continue;
      const usage = usageFromCodexPayload(data, maxTokens);
      if (usage) return usage;
      continue;
    }
    if (kind !== 'claude_protocol') continue;
    if (data.type !== 'assistant') continue;
    if (isSubagentFrame(data)) continue;
    const claudeUsage = usageFromClaudePayload(data);
    const used = contextTokensFromUsage(claudeUsage);
    if (used > 0) return deriveContextUsage(used, maxTokens, deriveCacheUsage(claudeUsage));
  }
  return null;
}
